import {useDebugValue, useMemo} from 'react';
import {useSelector} from 'react-redux';

export function useTracedSelector<TArgs extends readonly any[], TState extends object = {}, TSelected = unknown>(
    selector: (state: TState, ...args: TArgs) => TSelected,
    ...args: TArgs
) {
    const memoized = useMemo(() => createTracedSelector(selector, ...args), [selector, ...args]);
    useDebugValue(memoized, debugTracedSelector);

    const value = useSelector(memoized);
    return value;
}

export function bindTracedSelector<TArgs extends readonly any[], TState extends object = {}, TSelected = unknown>(
    selector: (state: TState, ...args: TArgs) => TSelected
) {
    return (...args: TArgs): TSelected => {
        const memoized = useMemo(() => createTracedSelector(selector, ...args), [...args]);
        useDebugValue(memoized, debugTracedSelector);

        const value = useSelector(memoized);
        return value;
    };
}

function debugTracedSelector(m: ReturnType<typeof createTracedSelector<any, any>>) {
    const cache = m.getCache();
    return cache && [cache.value, ...Object.keys(cache.keys)];
}

export function createTracedSelector<TArgs extends readonly any[], TState extends object = {}, TSelected = unknown>(
    selector: (state: TState, ...args: TArgs) => TSelected,
    ...args: TArgs
) {
    if (isTracedSelector(selector)) {
        return selector as unknown as typeof tracedSelector;
    }

    let cache: null | TraceCache<TSelected, TState> = null;

    const tracedSelector = (state: TState) => {
        if (cache != null) {
            if (cache.isValid(state)) {
                return cache.value;
            } else {
                cache = null;
            }
        }

        const keys = TraceCache.emptyKeys<TState>();
        const tracedState = new Proxy(state, {
            get(target, prop: string) {
                const key = (keys[prop] = prop as keyof TState);
                const val = target[key];
                return val;
            },
        });

        cache = new TraceCache(selector(tracedState, ...args), state, keys);
        return cache.value;
    };

    tracedSelector.getCache = () => cache;

    return tracedSelector;
}

function isTracedSelector(selector: (...args: any[]) => any) {
    try {
        return (
            'getCache' in selector && selector.getCache instanceof Function && selector.getCache() instanceof TraceCache
        );
    } catch {
        return false;
    }
}

class TraceCache<TSelected, TState> {
    static emptyKeys<TState>() {
        return {} as TraceCache.Keys<TState>;
    }

    constructor(
        readonly value: TSelected,
        readonly state: TState,
        readonly keys: TraceCache.Keys<TState>
    ) {}

    isValid(state: TState) {
        for (const key of Object.values(this.keys)) {
            if (state[key] !== this.state[key]) {
                return false;
            }
        }
        return true;
    }
}
namespace TraceCache {
    export type Keys<TState> = Record<string, keyof TState>;
}
